import React, { useEffect, useState, useCallback } from "react";
import { api, formatDateTime, formatApiError } from "../lib/api";
import { PageHeader, LoadingRows, EmptyState, StatusBadge, KPICard } from "../components/common";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { RefreshCw, Wifi, WifiOff, Battery, Zap, MapPin, Link2, Loader2, ArrowUpCircle, ArrowDownCircle, ServerCog } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const REFRESH_MS = 45000;

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function IoTDevices() {
  const [devices, setDevices] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1 });
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [linkDialog, setLinkDialog] = useState(null); // {device}
  const [assets, setAssets] = useState([]);
  const [assetChoice, setAssetChoice] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const [devRes, kpiRes] = await Promise.all([
        api.get("/iot/devices", { params: { page_number: 1, page_size: 50 } }),
        api.get("/iot/kpis"),
      ]);
      setDevices(devRes.data.devices || []);
      setMeta({ total: devRes.data.total_records, page: devRes.data.page_number });
      setKpis(kpiRes.data);
      setFetchedAt(new Date());
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(formatApiError(d?.error || d) || e.message);
    } finally { setLoading(false); setBusy(false); }
  }, []);

  useEffect(() => {
    api.get("/iot/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/assets", { params: { limit: 500 } }).then((r) => setAssets(r.data.items));
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const openLink = (device) => { setAssetChoice(""); setLinkDialog({ device }); };
  const saveLink = async () => {
    if (!assetChoice) { toast.error("Pick an asset"); return; }
    try {
      await api.post(`/iot/assets/${assetChoice}/link`, null, {
        params: { manufacturer_device_id: linkDialog.device.device_id },
      });
      toast.success("Asset linked to device");
      setLinkDialog(null);
      api.get("/assets", { params: { limit: 500 } }).then((r) => setAssets(r.data.items));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <LoadingRows rows={6} />;

  return (
    <>
      <PageHeader
        title="IoT Devices"
        description="Live snapshots from the Flexi Twin manufacturer API — displayed via the RushGro backend proxy."
        actions={<Button variant="outline" onClick={load} disabled={busy} data-testid="iot-devices-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>}
      />

      {!status?.configured && (
        <Card className="p-4 rounded-xl border-amber-500/40 flex items-start gap-3">
          <ServerCog className="w-4 h-4 text-amber-500 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium">IoT provider not configured</div>
            <div className="text-xs text-muted-foreground">Set <code>IOT_INTEGRATION_ID</code> and <code>IOT_API_KEY</code> on the backend to connect.</div>
          </div>
        </Card>
      )}

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KPICard label="Total Devices" value={kpis.total ?? 0} icon={Battery} accent="blue" testid="iot-kpi-total" />
          <KPICard label="Online" value={kpis.online ?? 0} icon={Wifi} accent="emerald" testid="iot-kpi-online" />
          <KPICard label="Offline" value={kpis.offline ?? 0} icon={WifiOff} accent="slate" testid="iot-kpi-offline" />
          <KPICard label="Charging" value={kpis.charging ?? 0} icon={ArrowUpCircle} accent="blue" />
          <KPICard label="Discharging" value={kpis.discharging ?? 0} icon={ArrowDownCircle} accent="amber" />
        </div>
      )}

      {err ? (
        <Card className="p-5 rounded-xl border-rose-500/40">
          <div className="text-sm text-rose-500">IoT provider error: {err}</div>
          <Button variant="outline" size="sm" className="mt-3" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry</Button>
        </Card>
      ) : devices.length === 0 ? (
        <EmptyState title="No devices returned by the IoT API" description="Check the Integration ID / API key on the backend." />
      ) : (
        <Card className="rounded-xl">
          <div className="p-3 border-b flex items-center justify-between text-xs text-muted-foreground">
            <div>{meta.total} device{meta.total === 1 ? "" : "s"} · showing {devices.length}</div>
            <div>Last updated: {fetchedAt ? fetchedAt.toLocaleTimeString() : "—"} · auto-refresh {REFRESH_MS / 1000}s</div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>SoC</TableHead>
                  <TableHead>Voltage</TableHead>
                  <TableHead>Current</TableHead>
                  <TableHead>Power</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Last Ping</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map((d) => {
                  const linkedAsset = assets.find((a) => a.manufacturer_device_id === d.device_id);
                  return (
                    <TableRow key={d.device_id} data-testid={`iot-row-${d.device_id}`}>
                      <TableCell>
                        <div className="font-medium">{d.name || d.unique_id || d.device_id}</div>
                        <div className="text-xs text-muted-foreground">#{d.device_id}</div>
                        {linkedAsset && <div className="text-[10px] text-emerald-500 mt-0.5">↔ {linkedAsset.serial_number}</div>}
                      </TableCell>
                      <TableCell>
                        {d.status === "online" ? (
                          <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 gap-1"><Wifi className="w-3 h-3" /> Online</Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1"><WifiOff className="w-3 h-3" /> Offline</Badge>
                        )}
                      </TableCell>
                      <TableCell>{d.soc != null ? `${d.soc}%` : "—"}</TableCell>
                      <TableCell>{d.voltage != null ? `${d.voltage} V` : "—"}</TableCell>
                      <TableCell>{d.current != null ? `${d.current} A` : "—"}</TableCell>
                      <TableCell>{d.power != null ? `${d.power} W` : "—"}</TableCell>
                      <TableCell>
                        {(d.latitude != null && d.longitude != null) ? (
                          <a target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${d.latitude},${d.longitude}`}
                             className="text-xs text-blue-500 hover:underline flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {d.latitude.toFixed(3)}, {d.longitude.toFixed(3)}
                          </a>
                        ) : "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{timeAgo(d.last_updated)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openLink(d)} data-testid={`iot-link-${d.device_id}`}>
                          <Link2 className="w-3.5 h-3.5 mr-1" /> {linkedAsset ? "Relink" : "Link"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className="p-2 border-t text-[10px] text-muted-foreground">
            Fields not provided by Flexi Twin: SoH, Temperature, Faults/Alarms, History.
          </div>
        </Card>
      )}

      <Dialog open={!!linkDialog} onOpenChange={(o) => !o && setLinkDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link device to a RushGro asset</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <div>Attach <span className="font-mono">#{linkDialog?.device?.device_id}</span> ({linkDialog?.device?.name}) to:</div>
            <div className="space-y-1">
              <Label>Asset</Label>
              <Select value={assetChoice} onValueChange={setAssetChoice}>
                <SelectTrigger data-testid="iot-link-asset-select"><SelectValue placeholder="Pick asset…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {assets.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.serial_number} — {a.model}{a.manufacturer_device_id ? ` (currently linked to #${a.manufacturer_device_id})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialog(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={saveLink} data-testid="iot-link-save">Save link</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
